import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import Layout from '../components/layout/Layout';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${theme.colors.text};
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 3rem;
  scroll-margin-top: 5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${theme.colors.secondary};
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${theme.colors.primary};
`;

const Content = styled.div`
  line-height: 1.7;
  color: ${theme.colors.text};
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  text-align: center;
`;

const TableHead = styled.thead`
  background-color: ${theme.colors.background};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${theme.colors.background};
  }
`;

const TableHeader = styled.th`
  padding: 12px 8px;
  border: 1px solid ${theme.colors.border};
  font-weight: bold;
`;

const TableCell = styled.td`
  padding: 10px 8px;
  border: 1px solid ${theme.colors.border};
`;

const LastUpdated = styled.div`
  font-style: italic;
  color: ${theme.colors.muted};
  margin-bottom: 2rem;
  text-align: right;
`;

const TermsPage: React.FC = () => {
  return (
    <Layout>
      <Helmet>
        <title>Vilkår og betingelser | Tryllepapp</title>
        <meta name="description" content="Les våre vilkår og betingelser for kjøp og salg av Magic: The Gathering-kort hos Tryllepapp." />
        <link rel="canonical" href="https://selg.tryllepapp.no/terms" />
      </Helmet>
      <Container>
        <Title>Våre betingelser</Title>
        
        <LastUpdated>Sist oppdatert: 27. mai 2025</LastUpdated>
        
        <Section>
          <SectionTitle>1. Introduksjon</SectionTitle>
          <Content>
            <p>Velkommen til Tryllepapp! Disse vilkårene og betingelsene gjelder for nettstedet www.tryllepapp.no som drives av Tryllepapp AS.</p>
            
            <p>Ved å få tilgang til eller bruke vår tjeneste, samtykker du i å være bundet av disse vilkårene. Vennligst les disse vilkårene nøye før du bruker nettstedet vårt.</p>
          </Content>
        </Section>
        
        <Section>
          <SectionTitle>2. Bruk av nettstedet</SectionTitle>
          <Content>
            <p>Ved å bruke vårt nettsted bekrefter du at:</p>
            <ul>
              <li>All informasjon du gir oss er sann, nøyaktig og fullstendig</li>
              <li>Du er over 18 år eller har foresattes samtykke til å bruke nettstedet</li>
              <li>Din bruk av tjenesten vil ikke bryte noen gjeldende lover eller forskrifter</li>
              <li>Du har lest disse vilkårene og betingelsene og ved å bruke vår tjeneste så aksepterer du disse.</li>
            </ul>

            <p>Vi forbeholder oss retten til å nekte tjenesten til hvem som helst av hvilken som helst grunn.</p>
          </Content>
        </Section>
        
        <Section>
          <SectionTitle>3. Personvern og datasikkerhet</SectionTitle>
          <Content>
            <p>Vi respekterer ditt personvern og overholder alle gjeldende personvernlover.</p>
          </Content>
        </Section>
        
        <Section>
          <SectionTitle>4. Ansvarsfraskrivelse</SectionTitle>
          <Content>
            <p>Tryllepapp leverer dette nettstedet "som det er" og gir ingen garantier, verken uttrykte eller underforståtte, inkludert, men ikke begrenset til, underforståtte garantier om salgbarhet og egnethet for et bestemt formål.</p>
            
            <p>Vi er ikke ansvarlige for indirekte, tilfeldige, spesielle eller følgeskader som oppstår som følge av kjøp eller bruk av våre produkter.</p>
            
            <p>Magic: The Gathering og alle tilhørende karakterer, logoer og distinktive design er opphavsrettslig beskyttet av Wizards of the Coast. Tryllepapp er ikke tilknyttet eller godkjent av Wizards of the Coast.</p>
          </Content>
        </Section>
        
        <Section>
          <SectionTitle>5. Endringer i vilkårene</SectionTitle>
          <Content>
            <p>Vi forbeholder oss retten til å oppdatere eller endre disse vilkårene når som helst uten forhåndsvarsel. Endringer trer i kraft umiddelbart etter at de er publisert på nettstedet.</p>
            
            <p>Din fortsatte bruk av nettstedet etter slike endringer betyr at du aksepterer de oppdaterte vilkårene.</p>
          </Content>
        </Section>

        <Section>
          <SectionTitle>6. Priser</SectionTitle>
          <Content>
            <p>Fraskriver oss ansvar for eventuelle feil innlagte priser, og reserverer oss retten til å avvise bestillinger med feil priser.</p>
            
            <p>Dette er spesielt aktuelt når det er spesielle situasjoner slik som at et kort bannes i et format. Dersom dette skjer og det kortet allerede
            er på vei til oss så vil vi måtte oppdatere prisen på dette kortet basert på de nye markedsforholdene.</p>
          </Content>
        </Section>
        
        <Section id="section-7">
          <SectionTitle>7. Selge kort til oss</SectionTitle>
          <Content>
            <p>Alle innkjøpspriser i buylisten er for kort i NM tilstand, og dersom kortet er i dårligere tilstand vil tilbudet vårt bli redusert ut fra følgende tabell:</p>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Innkjøpspris</TableHeader>
                  <TableHeader>EX</TableHeader>
                  <TableHeader>GD</TableHeader>
                  <TableHeader>LP</TableHeader>
                  <TableHeader>PL</TableHeader>                  
                </TableRow>
              </TableHead>
              <tbody>
                <TableRow>
                  <TableCell>Lavere enn €15</TableCell>
                  <TableCell>80%</TableCell>
                  <TableCell>70%</TableCell>
                  <TableCell>50%</TableCell>
                  <TableCell>40%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>€15.00 - €24.99</TableCell>
                  <TableCell>85%</TableCell>
                  <TableCell>70%</TableCell>
                  <TableCell>50%</TableCell>
                  <TableCell>40%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>€25.00 - €99.99</TableCell>
                  <TableCell>85%</TableCell>
                  <TableCell>75%</TableCell>
                  <TableCell>65%</TableCell>
                  <TableCell>55%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>€100.00 +</TableCell>
                  <TableCell>90%</TableCell>
                  <TableCell>80%</TableCell>
                  <TableCell>70%</TableCell>
                  <TableCell>60%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Alle Alpha/Beta/Unlimited</TableCell>
                  <TableCell>80%</TableCell>
                  <TableCell>60%</TableCell>
                  <TableCell>40%</TableCell>
                  <TableCell>30%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Foils</TableCell>
                  <TableCell>75%</TableCell>
                  <TableCell>50%</TableCell>
                  <TableCell>30%</TableCell>
                  <TableCell>20%</TableCell>
                </TableRow>
              </tbody>
            </Table>
            
            <p>Alle priser i vår buylist er indikative, og er ikke bindende.</p>
            <p>Vi kjøper ikke kort som er skadet, altså i tilstand Poor per cardmarkets condition guide.</p>
            <p>Etter at kortene er sendt oss for vurdering vil du motta et endelig tilbud, dersom det er avvik i pris eller tilstand.</p>
            <p>Kjøpsordre med mindre enn 20% samlet trekk i pris vil bli behandlet automatisk. For kjøpsordre med mer enn 20% trekk vil vi sende deg en e-post først. Dersom vi ikke hører fra deg innen 48 timer, vil vi gå videre med å behandle kjøpet.</p>
            <p>For fullstendig informasjon om innkjøp og vurdering av kort, vennligst besøk vår buylist-side.</p>
            <p>Vi kjøper også hele samlinger, ta kontakt med så mye informasjon som mulig for å få et grovt estimat.
              Ta så mange bilder som mulig eller send over liste fra deckbox e.l.
            </p>
            
            <p>Neste steg da blir isåfall at det sendes inn til oss for et bindende tilbud.</p>
            <p>Vi gir mellom 60-80% av markedspris, avhengig av nåværende inventar og likviditet på det som selges. </p>
            <p>Ved vurdering av samlinger sammenligner vi med prisen på kort i den tilstanden kortene faktisk er i. Tilstanden er dermed
              allerede priset inn, og tabellen over kommer ikke i tillegg — den gjelder kjøpsordre gjennom buylisten, hvor alle priser oppgis for NM.
            </p>
            <p>Vi har ikke mulighet til å betale for kort som ikke er i forsendelsen vi mottar når vi blir tilsendt kort, selv om 
              de var en del av kjøpsordren. Vær snill å forsikre dere om at det er samsvar mellom kjøpsordren og det som sendes inn. 
            </p>
            <p> Vi kjøper også forseglede produkter slik som booster boxes o.l. ta kontakt for et tilbud. For engelske produkter produsert
              etter 2015 er tilbudet som regel 75-90% av laveste pris på Cardmarket. Eldre forseglede produkter kjøper vi også, til andre
              satser — ta kontakt for et tilbud.
            </p>
            
          </Content>
        </Section>
        <Section>
          <SectionTitle>8. Kontaktinformasjon</SectionTitle>
          <Content>
            <p>E-post: kundeservice@tryllepapp.no</p>
          </Content>
        </Section>
        <Section>
          <SectionTitle>9. Gradering</SectionTitle>
          <Content>
            <p>Vi er veldig strenge graderere og følger graderingsstandarden definert av Cardmarket. Du kan lese deres
              fullstendige tilstandsguide her: <a href="https://help.cardmarket.com/en/CardCondition" target="_blank"
                                                  rel="noopener noreferrer">Cardmarket Condition Guide</a>.</p>
            <p>Dette gjelder begge veier, så vi er meget strenge med både det vi kjøper og det vi selger. Dersom du ikke er fornøyd så er det 14 dagers angrerett.</p>
            <p>Vi forhandler ikke på gradering eller pris.</p>
          </Content>
        </Section>
      </Container>
    </Layout>
  );
};

export default TermsPage;
